/**
 * GstSyncDialog — Sync real GST return filing status from the GST portal
 * (via gstinapi.in) for every GSTIN on a GST return subtask.
 *
 * Flow:
 *   1. Preview  — shows how many GSTINs will be checked vs skipped (already
 *                 portal-filed) and the estimated credit cost before spending.
 *   2. Sync     — fetches live status, persists portal result, returns a
 *                 team-vs-portal comparison.
 *   3. Compare  — highlights mismatches; user clicks "Accept" to adopt the
 *                 portal value into the team status (never auto-overwritten).
 */
import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, AlertTriangle, CheckCircle2, Loader2, Info, Check } from 'lucide-react';
import { tasksAPI } from '@/lib/api';
import { toast } from 'sonner';

export const GstSyncDialog = ({ open, onOpenChange, taskId, subtaskId, subtaskTitle, onChanged }) => {
  const [phase, setPhase] = useState('preview'); // preview | results
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [accepted, setAccepted] = useState({}); // key -> true
  const [acceptingKey, setAcceptingKey] = useState(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await tasksAPI.gstSyncPreview(taskId, subtaskId);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load sync preview');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [taskId, subtaskId, onOpenChange]);

  // Reset + load preview whenever opened
  React.useEffect(() => {
    if (open) {
      setPhase('preview');
      setResult(null);
      setAccepted({});
      loadPreview();
    }
  }, [open, loadPreview]);

  const runSync = async () => {
    setLoading(true);
    try {
      const { data } = await tasksAPI.gstSync(taskId, subtaskId);
      setResult(data);
      setPhase('results');
      if (data.mock) {
        toast.warning('Showing DEMO data — add your GST API key to sync real status');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setLoading(false);
    }
  };

  // All mismatch cells flattened for the comparison table
  const mismatchRows = React.useMemo(() => {
    if (!result) return [];
    const rows = [];
    result.results.forEach(r => {
      (r.periods || []).forEach(p => {
        if (!p.match) {
          rows.push({ ...p, client_id: r.client_id, gstin: r.gstin, client_name: r.client_name });
        }
      });
    });
    return rows;
  }, [result]);

  const acceptPortal = async (row) => {
    const key = `${row.client_id}|${row.gstin}|${row.period}`;
    setAcceptingKey(key);
    try {
      const newStatus = row.portal_status === 'filed' ? 'filed' : 'not_started';
      await tasksAPI.updateSubtaskClientPeriodStatus(taskId, subtaskId, row.client_id, row.period, { status: newStatus }, row.gstin);
      setAccepted(prev => ({ ...prev, [key]: true }));
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update status');
    } finally {
      setAcceptingKey(null);
    }
  };

  const acceptAllFiled = async () => {
    const pending = mismatchRows.filter(r => r.portal_status === 'filed' && !accepted[`${r.client_id}|${r.gstin}|${r.period}`]);
    for (const row of pending) {
      // eslint-disable-next-line no-await-in-loop
      await acceptPortal(row);
    }
    toast.success(`Accepted ${pending.length} portal-filed update(s)`);
  };

  const pendingFiledCount = mismatchRows.filter(r => r.portal_status === 'filed' && !accepted[`${r.client_id}|${r.gstin}|${r.period}`]).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="gst-sync-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-primary" /> Sync {subtaskTitle} status from GST portal
          </DialogTitle>
          <DialogDescription>
            Fetches live filing status for every GSTIN on this subtask and verifies it against your team's entries.
          </DialogDescription>
        </DialogHeader>

        {/* PREVIEW PHASE */}
        {phase === 'preview' && (
          <div className="space-y-4" data-testid="gst-sync-preview">
            {loading || !preview ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking…
              </div>
            ) : (
              <>
                {preview.mock && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="gst-sync-mock-banner">
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span><strong>Demo mode:</strong> no GST API key is configured, so this will return <strong>sample</strong> status. Add a key to sync real portal data.</span>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border p-3 text-center">
                    <div className="text-2xl font-bold" data-testid="gst-sync-total">{preview.total_gstins}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">GSTINs on subtask</div>
                  </div>
                  <div className="rounded-lg border p-3 text-center">
                    <div className="text-2xl font-bold text-sky-600" data-testid="gst-sync-tosync">{preview.to_sync}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">To be synced</div>
                  </div>
                  <div className="rounded-lg border p-3 text-center">
                    <div className="text-2xl font-bold text-emerald-600" data-testid="gst-sync-skipped">{preview.skipped_filed}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Skipped (already filed)</div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Approx. <strong>{preview.est_credits}</strong> credit(s) will be used (1 per GSTIN checked). GSTINs already confirmed filed on the portal are skipped.
                </p>
              </>
            )}
          </div>
        )}

        {/* RESULTS PHASE */}
        {phase === 'results' && result && (
          <div className="space-y-3" data-testid="gst-sync-results">
            {result.mock && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span><strong>Demo data shown.</strong> Add a GST API key to sync real portal status.</span>
              </div>
            )}
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="secondary" className="bg-sky-50 text-sky-700">{result.summary.synced} synced</Badge>
              <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">{result.summary.skipped_filed} skipped</Badge>
              <Badge variant="secondary" className="bg-amber-50 text-amber-700">{result.summary.mismatches} mismatch(es)</Badge>
              {result.summary.errors > 0 && <Badge variant="secondary" className="bg-red-50 text-red-700">{result.summary.errors} error(s)</Badge>}
              <Badge variant="outline">{result.summary.credits_used} credit(s) used</Badge>
            </div>

            {mismatchRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center gap-2" data-testid="gst-sync-allmatch">
                <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                <p className="text-sm font-medium">Everything matches the portal</p>
                <p className="text-xs text-muted-foreground">Your team's status agrees with the GST portal for all checked periods.</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-amber-50/60 border-b">
                  <span className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> {mismatchRows.length} mismatch(es) to review
                  </span>
                  {pendingFiledCount > 0 && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={acceptAllFiled} data-testid="gst-sync-accept-all">
                      Accept all portal-filed ({pendingFiledCount})
                    </Button>
                  )}
                </div>
                <div className="max-h-[320px] overflow-y-auto divide-y">
                  {mismatchRows.map(row => {
                    const key = `${row.client_id}|${row.gstin}|${row.period}`;
                    const isAccepted = accepted[key];
                    return (
                      <div key={key} className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`gst-sync-row-${key}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{row.client_name}</div>
                          <div className="text-muted-foreground truncate">{row.gstin} · {row.label}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="text-right">
                            <div className="text-[10px] text-muted-foreground">Team</div>
                            <Badge variant="secondary" className="text-[10px]">{row.team_status === 'filed' ? 'Filed' : row.team_status}</Badge>
                          </div>
                          <div className="text-right">
                            <div className="text-[10px] text-muted-foreground">Portal</div>
                            <Badge variant="secondary" className={`text-[10px] ${row.portal_status === 'filed' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              {row.portal_status === 'filed' ? `Filed${row.filing_date ? ' · ' + row.filing_date : ''}` : 'Not Filed'}
                            </Badge>
                          </div>
                          {isAccepted ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 font-medium w-[88px] justify-center"><Check className="h-3.5 w-3.5" /> Accepted</span>
                          ) : (
                            <Button size="sm" variant="outline" className="h-7 text-[11px] w-[88px]" disabled={acceptingKey === key} onClick={() => acceptPortal(row)} data-testid={`gst-sync-accept-${key}`}>
                              {acceptingKey === key ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Accept portal'}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {phase === 'preview' ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="gst-sync-cancel">Cancel</Button>
              <Button onClick={runSync} disabled={loading || !preview || preview.total_gstins === 0} data-testid="gst-sync-run">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Syncing…</> : <><RefreshCw className="h-4 w-4 mr-1.5" /> Sync now</>}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} data-testid="gst-sync-done">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
